import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LoadingScreen,
  MesaColumn,
  MesaTable,
  ResourceSection,
} from '@mesa-labs/mesa-ui';
import { types as api } from '@mesa-labs/mesa-api';

import { useDispatch, useSelector } from '../../../redux/hooks';
import { useGetVendorQuery, useGetVendorUserAccountActivityMetricsQuery } from '../../../redux/api/vendors';
import { DateTime } from 'luxon';
import { useTimezoneSelection } from '../../../utils';

const VendorActivityContainer = styled.div``;

const formatActivityDate = (value: api.ISODateString | undefined) => (
  value ? useTimezoneSelection(DateTime.fromISO(value)).toFormat("MMM d, yyyy 'at' h:mma") : 'Unknown'
);

const VendorActivityMesaColumns: MesaColumn<api.VendorUserAccountActivityMetricsResponse>[] = [
  {
    id: 'lastAuthSignIn',
    label: 'Last Auth Sign In',
    accessorFn: (row) => formatActivityDate(row.lastAuthSignIn),
  },
  {
    id: 'lastAuthSignOut',
    label: 'Last Auth Sign Out',
    accessorFn: (row) => formatActivityDate(row.lastAuthSignOut),
  },
  {
    id: 'lastAuthRefresh',
    label: 'Last Auth Refresh',
    accessorFn: (row) => formatActivityDate(row.lastAuthRefresh),
  },
  {
    id: 'lastResourceAccess',
    label: 'Last Resource Access',
    accessorFn: (row) => formatActivityDate(row.lastResourceAccess),
  },
  {
    id: 'numUniqueDaysResourceAccessLast90',
    label: 'Num Unique Days Resource Access Last 90',
    accessorFn: (row) => (row.numUniqueDaysResourceAccessLast90 ? row.numUniqueDaysResourceAccessLast90 : 'Unknown'),
  },
];

function VendorActivityPage(): React.ReactElement | null {
  const { id: vendorId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: vendor } = useGetVendorQuery({ id: vendorId! }, { skip: !vendorId });

  const vendorHasBeenActivated = useMemo(() => !!vendor?.activatedAt, [vendor]);

  const {
    data: vendorActivity,
    isLoading: vendorActivityLoading,
  } = useGetVendorUserAccountActivityMetricsQuery({ vendorId: vendorId! }, { skip: !vendorId });

  if (!vendor || vendorActivityLoading) {
    return <LoadingScreen />;
  }

  const vendorActivityTableRows = vendorActivity ? [vendorActivity] : [];

  return (
    <VendorActivityContainer>
      <>
        <ResourceSection
          title="Vendor Activity"
        >
          <MesaTable
            persistKey="vendorActivityPage"
            columns={VendorActivityMesaColumns}
            data={vendorActivityTableRows}
            dynamicColumnMode
            getRowId={(row) => row.vendorId}
          />
        </ResourceSection>
      </>
    </VendorActivityContainer>
  );
}

export default VendorActivityPage;
